import React, { useEffect, useMemo } from 'react';
import shortId from 'shortid';
import _get from 'lodash/get';
import { useDispatch } from 'react-redux';
import { useStatus } from 'resift';
import makeValidateMappings from 'fetches/makeValidateMappings';

import Loader from 'components/Loader';
import Mappings from './Mappings';

import SourceType from 'types/Source';
import MappingProps, * as MappingTypes from 'types/Mapping';
import {
  PROFILE_ATTR_MAPPING_TYPE_KEY,
  PRIMARY_MAPPING_TYPE_KEY,
  CLIENT_PRIMARY_KEY,
} from 'pages/Directory/consts';

interface Props {
  mappingAttributes: any;
  userFields: any;
  source: SourceType;
  setMappings: (batchActions: any[]) => void;
  setDirty: (dirty: boolean) => void;
}

function MappingsValidationFetcher({
  mappingAttributes,
  userFields,
  source,
  setMappings,
  setDirty,
}: Props) {
  const dispatch = useDispatch();
  const validateMappings = makeValidateMappings(source.id);
  const status = useStatus(validateMappings);

  const recommendedAttributes: Array<MappingTypes.RecommendedAttribute> = _get(
    mappingAttributes,
    'recommendedAttributes',
    [],
  );

  // (note - pearl) useMemo is necessary to preserve shortId during fetches
  const _proposedMappings = useMemo(
    () =>
      recommendedAttributes.map(attr => {
        const recommendedSourcePath = attr.sourcePath;

        // (notes - pearl) Attaching shortId for mappings validation
        const _referenceId = shortId();

        const _mapping = _get(attr, 'mapping', {
          source: recommendedSourcePath,
          destination: null,
          type: PROFILE_ATTR_MAPPING_TYPE_KEY,
        });

        return { ..._mapping, _referenceId };
      }),
    [recommendedAttributes],
  );

  const hasPrimaryMapping = _proposedMappings.find(m => m.destination === CLIENT_PRIMARY_KEY);

  const primaryKeyMappingReferenceId = shortId();
  // (note - pearl) useMemo is necessary to preserve shortId during fetches
  const proposedMappings = useMemo(() => {
    if (hasPrimaryMapping) {
      return _proposedMappings;
    }

    return [
      ..._proposedMappings,
      {
        source: null,
        type: PRIMARY_MAPPING_TYPE_KEY,
        destination: CLIENT_PRIMARY_KEY,
        isRequired: true,
        isUnique: true,
        _referenceId: primaryKeyMappingReferenceId,
      },
    ];
  }, [_proposedMappings, hasPrimaryMapping]) as MappingProps[];

  useEffect(() => {
    dispatch(validateMappings(proposedMappings));
  }, []);

  return (
    <Loader status={status}>
      {() => (
        <Mappings
          sourceMappings={proposedMappings}
          sourceFromProps={source}
          userFields={userFields}
          setMappings={setMappings}
          setDirty={setDirty}
        />
      )}
    </Loader>
  );
}

export default MappingsValidationFetcher;
